/**
 * Simplified Authentication Configuration
 *
 * DESIGN PRINCIPLES:
 * 1. Request ONLY the API scope (api://CLIENT_ID/access_api)
 * 2. User profile comes from ID token (no Microsoft Graph needed)
 * 3. Single resource = single token = simple and reliable
 * 4. No SSO complexity, no multi-resource juggling
 */

import Logger from '../utils/logger.js';

/**
 * Get Azure AD environment variables
 */
const getEnvVars = () => {
  const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
  const tenantId = import.meta.env.VITE_AZURE_TENANT_ID;
  const redirectUri = import.meta.env.VITE_AZURE_REDIRECT_URI;

  if (!clientId || !tenantId || !redirectUri) {
    throw new Error(
      'Missing required Azure AD environment variables. Please set VITE_AZURE_CLIENT_ID, VITE_AZURE_TENANT_ID, and VITE_AZURE_REDIRECT_URI'
    );
  }

  return { clientId, tenantId, redirectUri };
};

/**
 * MSAL Configuration
 */
export const getMsalConfig = () => {
  const { clientId, tenantId, redirectUri } = getEnvVars();

  return {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri,
      postLogoutRedirectUri: redirectUri,
      navigateToLoginRequestUrl: false
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false
    },
    system: {
      allowPlatformBroker: false,
      windowHashTimeout: 60000,
      iframeHashTimeout: 60000,
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          switch (level) {
            case 0: // Error
              Logger.error('[MSAL]', message);
              break;
            case 1: // Warning
              Logger.warn('[MSAL]', message);
              break;
            case 2: // Info
              Logger.info('[MSAL]', message);
              break;
            case 3: // Verbose
              Logger.debug('[MSAL]', message);
              break;
            default:
              break;
          }
        },
        // LogLevel: 0=Error, 1=Warning, 2=Info, 3=Verbose
        // Use Error level in production to reduce console noise
        logLevel: import.meta.env.MODE === 'production' ? 0 : 1,
        piiLoggingEnabled: false
      }
    }
  };
};

/**
 * Login Request - Request the specific API scope
 *
 * CRITICAL: Must request the specific scope api://CLIENT_ID/access_api (not .default)
 * to get an access token with the correct audience claim for our API.
 *
 * If we request .default, Azure AD issues a token for Microsoft Graph
 * (aud: 00000003-0000-0000-c000-000000000000) which our API will reject.
 *
 * The ID token will automatically contain user profile information.
 *
 * NOTE: We no longer use prompt: 'login' to allow SSO to work.
 * If SSO fails, MSAL will automatically prompt the user to sign in.
 */
export const getLoginRequest = () => {
  const { clientId } = getEnvVars();

  return {
    scopes: [
      `api://${clientId}/access_api` // Must match exposed API scope
    ],
    // NOTE: Do NOT specify prompt: 'login' - this prevents SSO from working
    // MSAL will handle prompting the user if needed
    // Optional: Add domain hint if configured to speed up SSO
    ...(import.meta.env.VITE_DOMAIN_HINT && {
      extraQueryParameters: {
        domain_hint: import.meta.env.VITE_DOMAIN_HINT
      }
    })
  };
};

/**
 * Silent SSO Request - Used for automatic sign-in without user interaction
 *
 * This request is used when attempting to silently authenticate a user
 * who is already signed into their Microsoft account in the browser.
 *
 * The domain_hint helps Azure AD identify which tenant to use for SSO.
 */
export const getSilentSSORequest = () => {
  const { clientId } = getEnvVars();

  return {
    scopes: [`api://${clientId}/access_api`],
    // Domain hint for SSO - tells Azure AD which domain to try
    ...(import.meta.env.VITE_DOMAIN_HINT && {
      extraQueryParameters: {
        domain_hint: import.meta.env.VITE_DOMAIN_HINT
      }
    })
  };
};

/**
 * API Request - Same as login request
 * We use the same scope for both login and API calls
 */
export const getApiRequest = () => getLoginRequest();

/**
 * Extract user profile from ID token claims
 * The ID token contains all the user information we need
 */
export const getUserFromIdToken = idTokenClaims => {
  if (!idTokenClaims) {
    Logger.warn('[AUTH] No ID token claims provided');
    return null;
  }

  return {
    id: idTokenClaims.oid || idTokenClaims.sub, // Object ID or Subject
    email: idTokenClaims.preferred_username || idTokenClaims.email || idTokenClaims.upn,
    name: idTokenClaims.name,
    givenName: idTokenClaims.given_name,
    familyName: idTokenClaims.family_name,
    roles: idTokenClaims.roles || [], // App roles from Azure AD
    // Additional claims that might be useful
    tenantId: idTokenClaims.tid,
    username: idTokenClaims.preferred_username
  };
};

// Export singleton instances
// NOTE: Direct objects instead of Proxy pattern for MSAL compatibility
// MSAL internally spreads/iterates objects, which doesn't work with Proxy get traps
let _msalConfig = null;
let _loginRequest = null;
let _apiRequest = null;
let _silentSSORequest = null;

export const msalConfig = (() => {
  if (!_msalConfig) _msalConfig = getMsalConfig();
  return _msalConfig;
})();

export const loginRequest = (() => {
  if (!_loginRequest) _loginRequest = getLoginRequest();
  return _loginRequest;
})();

export const apiRequest = (() => {
  if (!_apiRequest) _apiRequest = getApiRequest();
  return _apiRequest;
})();

export const silentSSORequest = (() => {
  if (!_silentSSORequest) _silentSSORequest = getSilentSSORequest();
  return _silentSSORequest;
})();
